import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { credentialRequestsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Users, 
  CreditCard,
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Upload,
  Camera
} from 'lucide-react';

const statusConfig = {
  PENDING_PAYMENT: { label: 'Pendiente de Pago', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  PAYMENT_CONFIRMED: { label: 'Pago Confirmado', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  PENDING_APPROVAL: { label: 'Pendiente Aprobación', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  APPROVED: { label: 'Aprobada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  REJECTED: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function CredentialRequestDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectComments, setRejectComments] = useState('');

  useEffect(() => {
    loadRequest();
  }, [id]);

  const loadRequest = async () => {
    try {
      setLoading(true);
      const response = await credentialRequestsAPI.getById(id);
      setRequest(response.data);
    } catch (error) {
      toast.error('Error al cargar la solicitud');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    setActionLoading(true);
    try {
      await credentialRequestsAPI.confirmPayment(id);
      toast.success('Pago confirmado exitosamente');
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar pago');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const response = await credentialRequestsAPI.approve(id);
      toast.success('Solicitud aprobada. Credenciales generadas.');
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar solicitud');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await credentialRequestsAPI.reject(id, rejectComments);
      toast.success('Solicitud rechazada');
      setShowRejectDialog(false);
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar solicitud');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoUpload = async (repIndex, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      await credentialRequestsAPI.uploadPhoto(id, repIndex, file);
      toast.success('Foto cargada exitosamente');
      loadRequest();
    } catch (error) {
      toast.error('Error al cargar la foto');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-EC', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  // Helper to convert static URLs to API file URLs
  const getFileUrl = (staticUrl) => {
    if (!staticUrl) return '';
    const backendUrl = getBackendUrl();
    // Convert /static/credentials/file.pdf to /api/files/credentials/file.pdf
    if (staticUrl.startsWith('/static/credentials/')) {
      const filename = staticUrl.replace('/static/credentials/', '');
      return `${backendUrl}/api/files/credentials/${encodeURIComponent(filename)}`;
    }
    // Convert /static/uploads/file.jpg to /api/files/uploads/file.jpg
    if (staticUrl.startsWith('/static/uploads/')) {
      const filename = staticUrl.replace('/static/uploads/', '');
      return `${backendUrl}/api/files/uploads/${encodeURIComponent(filename)}`;
    }
    return `${backendUrl}${staticUrl}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Solicitud no encontrada</p>
        <Button variant="link" onClick={() => navigate('/credentials')}>
          Volver a la lista
        </Button>
      </div>
    );
  }

  const status = statusConfig[request.status] || statusConfig.PENDING_PAYMENT;
  const StatusIcon = status.icon;
  const paymentInfo = request.payment_info || {};
  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="space-y-6" data-testid="credential-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/credentials')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">Solicitud de Credenciales</h1>
              {request.credential_number && (
                <Badge variant="outline" className="text-sm font-mono bg-primary/5 border-primary/20">
                  COT-{String(request.credential_number).padStart(4, '0')}
                </Badge>
              )}
            </div>
            <p className="text-slate-500">{paymentInfo.company_name}</p>
          </div>
        </div>
        <Badge className={`${status.color} flex items-center gap-1 text-base px-4 py-2`}>
          <StatusIcon className="w-4 h-4" />
          {status.label}
        </Badge>
      </div>

      {/* Admin Actions */}
      {isAdmin && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Acciones de Administrador</h3>
                <p className="text-sm text-slate-500">Gestione el estado de esta solicitud</p>
              </div>
              <div className="flex gap-2">
                {request.status === 'PENDING_PAYMENT' && (
                  <Button onClick={handleConfirmPayment} disabled={actionLoading} data-testid="confirm-payment-btn">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Confirmar Pago
                  </Button>
                )}
                {request.status === 'PENDING_APPROVAL' && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowRejectDialog(true)} 
                      disabled={actionLoading}
                      className="text-red-600 hover:text-red-700"
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                    <Button onClick={handleApprove} disabled={actionLoading} data-testid="approve-btn">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprobar y Generar Credenciales
                    </Button>
                  </>
                )}
                {request.status === 'APPROVED' && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Solicitud Completada
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5" />
              Datos de la Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-slate-500">Razón Social</p>
              <p className="font-medium">{paymentInfo.company_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">RUC</p>
              <p className="font-medium">{paymentInfo.ruc}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Línea Comercial</p>
              <p className="font-medium">{paymentInfo.commercial_line}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Categorías</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {(paymentInfo.product_categories || []).map((cat, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signer Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5" />
              Datos del Firmante
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-slate-500">Nombre Completo</p>
              <p className="font-medium">{paymentInfo.signer_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Cédula</p>
              <p className="font-medium">{paymentInfo.signer_id}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Email</p>
              <p className="font-medium">{paymentInfo.signer_email}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Contacto Farmaenlace</p>
              <p className="font-medium">{paymentInfo.farmaenlace_contact_name || '-'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Info */}
        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="w-5 h-5" />
              Detalle de Costos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-600">Credenciales</span>
              <span className="font-medium">{request.num_credentials}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Precio unitario</span>
              <span className="font-medium">${request.unit_price?.toFixed(2)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="text-primary">{formatCurrency(request.total_price)}</span>
            </div>
            <div className="text-xs text-slate-500 text-right">
              (Incluye 15% IVA)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agreement PDF */}
      {request.agreement_pdf_url && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5" />
              Cotización de Auspicio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-medium">
                    Cotización de Auspicio {request.credential_number ? `COT-${String(request.credential_number).padStart(4, '0')}` : ''}
                  </p>
                  <p className="text-sm text-slate-500">Documento PDF generado</p>
                </div>
              </div>
              <Button asChild data-testid="download-agreement-btn">
                <a href={getFileUrl(request.agreement_pdf_url)} target="_blank" rel="noopener noreferrer">
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Representatives */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5" />
            Representantes ({request.representatives?.length || 0})
          </CardTitle>
          <CardDescription>Lista de representantes que recibirán credenciales</CardDescription>
        </CardHeader>
        <CardContent>
          {request.representatives?.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 rounded-lg">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No hay representantes registrados</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {request.representatives?.map((rep, index) => (
                <div key={index} className="border rounded-lg p-4 text-center">
                  <div className="relative w-20 h-20 mx-auto mb-3">
                    {rep.photo_url ? (
                      <img 
                        src={getFileUrl(rep.photo_url)} 
                        alt={`${rep.name} ${rep.surname}`}
                        className="w-full h-full object-cover rounded-full border-2 border-primary"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-200 rounded-full flex items-center justify-center">
                        <User className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                    {(request.status === 'PENDING_PAYMENT' || request.status === 'PENDING_APPROVAL') && (
                      <label className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/80">
                        <Camera className="w-3 h-3 text-white" />
                        <input 
                          type="file" 
                          accept="image/jpeg,image/png" 
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(index, e)}
                        />
                      </label>
                    )}
                  </div>
                  <p className="font-medium">{rep.name} {rep.surname}</p>
                  {rep.id_number && (
                    <p className="text-sm text-slate-500">C.I.: {rep.id_number}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated Credentials */}
      {request.status === 'APPROVED' && request.credentials_urls && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-green-800">
              <CheckCircle className="w-5 h-5" />
              Credenciales Generadas
            </CardTitle>
            <CardDescription>Descargue las credenciales de cada representante</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {request.credentials_urls.map((cred, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    <span className="font-medium text-sm">{cred.name}</span>
                  </div>
                  <Button variant="outline" size="sm" asChild data-testid={`download-credential-${index}`}>
                    <a href={getFileUrl(cred.url)} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Creado: {formatDate(request.created_at)}</span>
            <span>Actualizado: {formatDate(request.updated_at)}</span>
            {request.approved_at && <span>Aprobado: {formatDate(request.approved_at)}</span>}
            {request.rejected_at && <span>Rechazado: {formatDate(request.rejected_at)}</span>}
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Solicitud</DialogTitle>
            <DialogDescription>
              Indique el motivo del rechazo. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Motivo del rechazo..."
            value={rejectComments}
            onChange={(e) => setRejectComments(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading}>
              {actionLoading ? 'Rechazando...' : 'Confirmar Rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
